"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Badge as UIBadge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Custom Badge component without hover effects
const Badge = ({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof UIBadge>) => {
  return (
    <UIBadge
      variant={variant}
      className={cn(
        className,
        variant === "secondary" && "hover:bg-secondary",
        variant === "default" && "hover:bg-primary",
        variant === "destructive" && "hover:bg-destructive",
        variant === "outline" && "hover:bg-transparent",
      )}
      {...props}
    />
  );
};
import {
  ArrowLeft,
  Pencil,
  Clock,
  Building,
  Mail,
  User,
  Shield,
} from "lucide-react";
import UserModal from "../user-modal";
import { format } from "date-fns";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { ROLES } from "@/lib/constants/roles";
import {
  FeatureAccessCard,
  type FeatureAccessCardProps,
} from "@/components/admin/users/feature-access-card";

export interface UserDetails {
  id: number;
  name: string | null;
  email?: string;
  user_id: string;
  role_id: number;
  venue_id: number | null;
  status_id: number;
  created_at: string;
  updated_at: string;
  roles?: { id: number; name: string };
  venues?: { id: number; name: string };
  statuses?: { id: number; name: string };
  user_venue_permissions?: Array<{
    venue_id: number;
    features?: string[];
    venues?: { id: number; name: string };
  }>;
}

interface AuditLog {
  id: number;
  action: string;
  details: any;
  created_at: string;
  user?: {
    name: string;
    email: string;
  };
}

interface Role {
  id: number;
  name: string;
}

interface Venue {
  id: number;
  name: string;
  status_id?: number;
}

interface Status {
  id: number;
  name: string;
}

export default function UserDetailsClient({
  userId,
  initialUser,
  backHref = "/superadmin/users",
  access,
}: {
  userId: string;
  /**
   * The user, loaded by the page. Given (booking portal): nothing is fetched
   * from the super admin APIs, and Edit User and the activity history, which
   * are super admin only, are left out.
   */
  initialUser?: UserDetails;
  backHref?: string;
  /** Overrides for the access card, e.g. which venues and switches. */
  access?: Partial<FeatureAccessCardProps>;
}) {
  const router = useRouter();
  const preloaded = initialUser !== undefined;
  const [user, setUser] = useState<UserDetails | null>(initialUser ?? null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(!preloaded);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);

  const fetchUserDetails = async () => {
    try {
      const response = await fetch(`/api/superadmin/users/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await fetch(
        `/api/superadmin/users/${userId}/audit-logs`,
      );
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
      }
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    }
  };

  // Server-loaded data changes on router.refresh(); keep it in step.
  useEffect(() => {
    if (initialUser) setUser(initialUser);
  }, [initialUser]);

  useEffect(() => {
    if (preloaded) return;
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch all data in parallel
        const [rolesRes, venuesRes, statusesRes] = await Promise.all([
          fetch("/api/superadmin/roles"),
          fetch("/api/superadmin/venues"),
          fetch("/api/superadmin/statuses?type=user"),
          fetchUserDetails(),
          fetchAuditLogs(),
        ]);

        const [rolesData, venuesData, statusesData] = await Promise.all([
          rolesRes.json(),
          venuesRes.json(),
          statusesRes.json(),
        ]);

        if (rolesRes.ok) setRoles(rolesData.roles || []);
        if (venuesRes.ok) setVenues(venuesData.venues || []);
        if (statusesRes.ok && statusesData.data) {
          // Filter out archived status (ID 3) and use data.all
          const userStatuses = statusesData.data.all || [];
          const filteredStatuses = userStatuses.filter(
            (status: Status) => status.id !== 3,
          );
          setStatuses(filteredStatuses);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  const getActionLabel = (action: string, details?: any) => {
    const labels: Record<string, string> = {
      user_created: "User Created",
      user_updated: "User Updated",
      user_deleted: "User Deactivated",
      user_invited: "User Invited",
      role_changed: "Role Changed",
      venues_updated: "Venues Updated",
      status_changed: "Status Changed",
      booking_activity:
        details?.activity_type
          ?.replace("_", " ")
          .replace(/\b\w/g, (l: string) => l.toUpperCase()) ||
        "Booking Activity",
    };
    return labels[action] || action;
  };

  const getActionColor = (action: string) => {
    const colors: Record<string, string> = {
      user_created:
        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30",
      user_updated:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30",
      user_deleted:
        "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30",
      role_changed:
        "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30",
      venues_updated:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30",
      status_changed:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/30",
      booking_activity:
        "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30",
    };
    return (
      colors[action] ||
      "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
    );
  };

  if (loading) {
    return (
      <div className="container py-8 flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container py-8">
        <div className="text-center">
          <p className="text-muted-foreground">User not found</p>
          <Button onClick={() => router.push(backHref)} className="mt-4">
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container py-8 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => router.push(backHref)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold">User Details</h1>
          </div>
          {!preloaded && (
            <Button onClick={() => setIsModalOpen(true)} className="gap-2">
              <Pencil className="h-4 w-4" />
              Edit User
            </Button>
          )}
        </div>

        {/* User Info Card */}
        <div className="bg-card border rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Name</span>
              </div>
              <p className="font-medium">{user.name || "N/A"}</p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Email</span>
              </div>
              <p className="font-medium">{user.email || "N/A"}</p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Role</span>
              </div>
              <p className="font-medium">
                {user.roles?.name
                  ?.replace("_", " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase()) || "N/A"}
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Venues</span>
              </div>
              <div>
                {user.role_id === 4 ? (
                  // For customers (role_id = 4), only show primary venue
                  user.venues?.name ? (
                    <Badge variant="secondary">{user.venues.name}</Badge>
                  ) : (
                    <span className="text-muted-foreground">
                      No venue assigned
                    </span>
                  )
                ) : // For staff users, show venue permissions or primary venue
                user.user_venue_permissions &&
                  user.user_venue_permissions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {user.user_venue_permissions.map((perm) => (
                      <Badge key={perm.venue_id} variant="secondary">
                        {perm.venues?.name || `Venue ${perm.venue_id}`}
                      </Badge>
                    ))}
                  </div>
                ) : user.venues?.name ? (
                  <Badge variant="secondary">{user.venues.name}</Badge>
                ) : (
                  <span className="text-muted-foreground">
                    No venues assigned
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Status</span>
              </div>
              <Badge
                className={
                  user.statuses?.name === "active"
                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30"
                    : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                }
              >
                {user.statuses?.name || "Unknown"}
              </Badge>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Created</span>
              </div>
              <p className="font-medium">
                {user.created_at
                  ? format(new Date(user.created_at), "PPP")
                  : "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* What booking staff can do at each of their venues */}
        {user.role_id === ROLES.BOOKING_STAFF && (
          <FeatureAccessCard
            key={user.user_venue_permissions?.map((p) => p.venue_id).join(",")}
            userInfoId={user.id}
            globalFeatures={(user.user_venue_permissions ?? []).flatMap(
              (p) => p.features ?? [],
            )}
            venues={(user.user_venue_permissions ?? []).map((p) => ({
              id: p.venue_id,
              name: p.venues?.name || `Venue ${p.venue_id}`,
              features: p.features ?? [],
            }))}
            {...access}
          />
        )}

        {/* Activity History */}
        {!preloaded && (
          <div className="bg-card border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Activity History</h2>

            {auditLogs.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No activity history available
              </p>
            ) : (
              <div className="space-y-4">
                {auditLogs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-start gap-4 pb-4 border-b last:border-0"
                  >
                    <Badge className={getActionColor(log.action)}>
                      {getActionLabel(log.action, log.details)}
                    </Badge>
                    <div className="flex-1">
                      <p className="text-sm">
                        {log.user?.name || "System"}
                        <span className="text-muted-foreground">
                          {" · "}
                          {format(new Date(log.created_at), "PPp")}
                        </span>
                      </p>
                      {log.action === "booking_activity" && log.details && (
                        <div className="text-xs text-muted-foreground mt-1">
                          <p>Booking #{log.details.booking_id}</p>
                          {log.details.description && (
                            <p className="mt-1">{log.details.description}</p>
                          )}
                        </div>
                      )}
                      {log.action !== "booking_activity" &&
                        log.details &&
                        Object.keys(log.details).length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {Object.entries(log.details).map(([key, value]) => (
                              <p key={key}>
                                {key}: {String(value)}
                              </p>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!preloaded && (
        <UserModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
          onSuccess={() => {
            fetchUserDetails();
            fetchAuditLogs();
            setIsModalOpen(false);
          }}
          roles={roles}
          venues={venues.filter(
            (venue) => venue.status_id === STATUS_IDS.VENUE_ACTIVE,
          )}
          statuses={statuses}
          user={user}
        />
      )}
    </>
  );
}
